import React from 'react';
import styled from 'styled-components';
import { Separator, Col, Row, FlexView } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import {
  EventRow,
  EventIcon,
  MsgContainer,
  MsgContainerAgent,
  MsgAvatar,
  MsgHead,
  MsgName,
  MsgRole,
  MsgTime,
  MsgBodyUser,
  MsgBodyBot,
  MsgBodyAgent,
  SystemMsg,
  NoteCard,
  NoteTag,
  AttachImage,
  FileCard,
  FileCardUser,
  FileIconWrap,
  FileIconWrapUser,
  FileName,
  SA_COLORS,
} from '../styled-comps';
import { SUPPORT_EMAIL_PREFIX } from '../constants';
import parse from 'html-react-parser';
import { getFileNameFromUrl, sanitizeHtml } from '../utils';
import Icon from './icon';
import MarkdownText from './markdown-text';

// ── Styled helpers ─────────────────────────────────────────────────────

const HtmlBody = styled.div`
  word-break: break-word;
`;

const EmailRecipientsInline = styled.div`
  border-bottom: 1px solid
    ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)')};
  padding-bottom: 6px;
  margin-bottom: 6px;
`;

const EmailRecipientLine = styled(Text)`
  && {
    font-size: 11px;
    color: ${({ $dark }) =>
      $dark ? 'rgba(255, 255, 255, 0.7)' : SA_COLORS.TEXT_SECONDARY};
    font-family: monospace;
    line-height: 1.5;
  }
`;

const EmailLabel = styled.span`
  display: inline-block;
  min-width: 32px;
`;

const EventTitle = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const EventSub = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const EventInfo = styled(Col)`
  && {
    gap: 2px;
    justify-content: center;
  }
`;

const MsgColumn = styled(FlexView)`
  && {
    gap: 6px;
    min-width: 0;
    flex: 1;
  }
`;

const SentByAnnotation = styled(Text)`
  && {
    font-size: 11px;
    font-style: italic;
    color: ${SA_COLORS.TEXT_TERTIARY};
    padding: 0 2px;
  }
`;

const SentByName = styled.span`
  font-weight: 600;
  font-style: normal;
  color: ${SA_COLORS.TEXT_SECONDARY};
`;

const StructuredButtonsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const StructuredBtn = styled.button`
  display: inline-block;
  padding: 5px 12px;
  font-size: 12px;
  color: ${SA_COLORS.INFO_600};
  background: transparent;
  border: 1px solid ${SA_COLORS.INFO_600};
  border-radius: 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${SA_COLORS.INFO_600}11;
  }
`;

const NoteHeader = styled(Row)`
  && {
    align-items: center;
    gap: 6px;
  }
`;

const NoteTitle = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA_COLORS.GOLD_900};
  }
`;

const NoteBody = styled(FlexView)`
  && {
    font-size: 13px;
    line-height: 1.5;
    color: ${SA_COLORS.GOLD_900};
  }
`;

const SystemBody = styled(Text)`
  && {
    font-size: 13px;
    line-height: 1.4;
  }
`;

// ── Role helpers ───────────────────────────────────────────────────────

const getRoleLabel = item => {
  if (!item.role) return '';
  if (item.who === 'bot') return 'AI bot';
  if (item.who === 'agent') return 'Agent';
  return 'Customer';
};

const getName = item => item.name || '';

// ── Event ──────────────────────────────────────────────────────────────

const EventItem = ({ item }) => (
  <EventRow>
    <EventIcon>
      <Icon name={item.icon || 'chat'} size={16} />
    </EventIcon>
    <EventInfo>
      <EventTitle>{item.title}</EventTitle>
      {item.sub && <EventSub>{item.sub}</EventSub>}
    </EventInfo>
  </EventRow>
);

// ── Attachment rendering ───────────────────────────────────────────────

const AttachmentContent = ({ item, isUser }) => {
  const hasMedia = item.mediaUrl && item.messageType !== 'TEXT';

  if (!hasMedia) {
    if (item.htmlContent) {
      return (
        <HtmlBody>
          {parse(sanitizeHtml(item.htmlContent), {
            replace: node => {
              if (node.name === 'img') {
                const { src, alt, width, height } = node.attribs || {};
                return (
                  <AttachImage
                    src={src}
                    alt={alt || ''}
                    width={width}
                    height={height}
                    onClick={() => window.open(src, '_blank')}
                  />
                );
              }
            },
          })}
        </HtmlBody>
      );
    }
    return item.body ? <MarkdownText>{item.body}</MarkdownText> : null;
  }

  const FileCardComp = isUser ? FileCardUser : FileCard;
  const IconWrapComp = isUser ? FileIconWrapUser : FileIconWrap;

  return (
    <>
      {item.messageType === 'IMAGE' ? (
        <AttachImage
          src={item.mediaUrl}
          alt={item.body || 'Image'}
          onClick={() => window.open(item.mediaUrl, '_blank')}
        />
      ) : (
        <FileCardComp href={item.mediaUrl} target="_blank" rel="noopener noreferrer">
          <IconWrapComp>
            <Icon name="link" size={16} />
          </IconWrapComp>
          <FileName>{getFileNameFromUrl(item.mediaUrl)}</FileName>
        </FileCardComp>
      )}
      {item.body && (
        <>
          <Separator height={6} transparent />
          <MarkdownText>{item.body}</MarkdownText>
        </>
      )}
    </>
  );
};

// ── Message ────────────────────────────────────────────────────────────

const MsgItem = ({ item, emailFrom, emailTo, onOptionSelect, userOnRight }) => {
  const isUser = item.who === 'user';
  const isBot = item.who === 'bot';
  const isAgent = item.who === 'agent';

  const rightSide = userOnRight ? isUser : !isUser;

  const Container = rightSide ? MsgContainerAgent : MsgContainer;

  const BodyComponent = isUser ? MsgBodyUser : isBot ? MsgBodyBot : MsgBodyAgent;

  const avatarVariant = isUser ? 'user' : isBot ? 'bot' : 'agent';
  const avatarContent = isBot ? (
    <Icon name="sparks" size={14} />
  ) : (
    item.initials ||
    getName(item)
      .slice(0, 2)
      .toUpperCase()
  );

  const roleLabel = getRoleLabel(item);

  return (
    <Container flexDirection={rightSide ? 'row-reverse' : 'row'}>
      <MsgAvatar $variant={avatarVariant}>{avatarContent}</MsgAvatar>
      <MsgColumn alignItems={rightSide ? 'end' : 'start'}>
        <MsgHead
          alignItems="center"
          justifyContent={rightSide ? 'flex-end' : 'flex-start'}
        >
          <MsgName>{getName(item)}</MsgName>
          {roleLabel && <MsgRole>{roleLabel}</MsgRole>}
          {item.ts && <MsgTime>{item.ts}</MsgTime>}
        </MsgHead>
        <Separator height={4} transparent />
        <BodyComponent $rightSide={rightSide}>
          {emailFrom && (isAgent || isUser) && (
            <EmailRecipientsInline $dark={isAgent}>
              <EmailRecipientLine $dark={isAgent}>
                <EmailLabel>From</EmailLabel>:{' '}
                {isAgent ? emailFrom : item.senderEmail || emailTo}
              </EmailRecipientLine>
              <EmailRecipientLine $dark={isAgent}>
                <EmailLabel>To</EmailLabel>:{' '}
                {isAgent
                  ? emailTo
                  : item.emailToList
                  ? item.emailToList
                      .map(e => (e.startsWith(SUPPORT_EMAIL_PREFIX) ? emailFrom || e : e))
                      .join(', ')
                  : emailFrom}
              </EmailRecipientLine>
              {item.emailCc && item.emailCc.length > 0 && (
                <EmailRecipientLine $dark={isAgent}>
                  <EmailLabel>CC</EmailLabel>:{' '}
                  {item.emailCc
                    .map(e => (e.startsWith(SUPPORT_EMAIL_PREFIX) ? emailFrom || e : e))
                    .join(', ')}
                </EmailRecipientLine>
              )}
              {item.emailBcc && item.emailBcc.length > 0 && (
                <EmailRecipientLine $dark={isAgent}>
                  <EmailLabel>BCC</EmailLabel>: {item.emailBcc.join(', ')}
                </EmailRecipientLine>
              )}
            </EmailRecipientsInline>
          )}
          <AttachmentContent item={item} isUser={isUser} />
          {item.structuredData?.options && onOptionSelect && (
            <StructuredButtonsWrap>
              {item.structuredData.options.map((opt, idx) => (
                <StructuredBtn
                  key={idx}
                  onClick={onOptionSelect ? () => onOptionSelect(opt) : undefined}
                >
                  {opt.label || opt.value || ''}
                </StructuredBtn>
              ))}
            </StructuredButtonsWrap>
          )}
        </BodyComponent>
        {isUser && item.sentBy && (
          <SentByAnnotation>
            Sent by <SentByName>{item.sentByName || item.sentBy}</SentByName> on behalf of
            user
          </SentByAnnotation>
        )}
      </MsgColumn>
    </Container>
  );
};

// ── System ─────────────────────────────────────────────────────────────

const SystemItem = ({ item }) => {
  const tint = item.tint || 'info';
  const iconName = tint === 'error' ? 'close' : tint === 'warning' ? 'hint' : 'hint';

  return (
    <SystemMsg $variant={tint}>
      <Icon name={iconName} size={16} />
      <SystemBody>
        {item.title && (
          <>
            <Text variant="body2">{item.title}</Text>
            <Separator height={4} transparent />
          </>
        )}
        {item.sub && (
          <>
            <Text variant="caption">{item.sub}</Text>
            <Separator height={4} transparent />
          </>
        )}
        {item.body && <MarkdownText>{item.body}</MarkdownText>}
      </SystemBody>
    </SystemMsg>
  );
};

// ── Note ───────────────────────────────────────────────────────────────

const NoteEmailMeta = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 6px;
  margin-bottom: 6px;
`;

const NoteEmailLine = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA_COLORS.GOLD_700};
    font-family: monospace;
    line-height: 1.5;
  }
`;

const NoteItem = ({ item }) => {
  const isEmailNote = !!(item.emailToList || item.emailSubject);

  return (
    <NoteCard>
      <NoteHeader>
        <Icon name={isEmailNote ? 'send' : 'lock'} size={14} color={SA_COLORS.GOLD_700} />
        <NoteTitle>{item.author || 'Internal note'}</NoteTitle>
      </NoteHeader>
      <NoteTag>Internal — not visible to customer</NoteTag>
      {isEmailNote && (
        <NoteEmailMeta>
          {item.senderEmail && (
            <NoteEmailLine>
              <EmailLabel>From</EmailLabel>: {item.senderEmail}
            </NoteEmailLine>
          )}
          {item.emailToList && (
            <NoteEmailLine>
              <EmailLabel>To</EmailLabel>: {item.emailToList.join(', ')}
            </NoteEmailLine>
          )}
          {item.emailCc && item.emailCc.length > 0 && (
            <NoteEmailLine>
              <EmailLabel>CC</EmailLabel>: {item.emailCc.join(', ')}
            </NoteEmailLine>
          )}
          {item.emailBcc && item.emailBcc.length > 0 && (
            <NoteEmailLine>
              <EmailLabel>BCC</EmailLabel>: {item.emailBcc.join(', ')}
            </NoteEmailLine>
          )}
          {item.emailSubject && (
            <NoteEmailLine>
              <EmailLabel>Subject</EmailLabel>: {item.emailSubject}
            </NoteEmailLine>
          )}
        </NoteEmailMeta>
      )}
      {item.mediaUrl && item.messageType === 'IMAGE' && (
        <AttachImage
          src={item.mediaUrl}
          alt={item.body || 'Image'}
          onClick={() => window.open(item.mediaUrl, '_blank')}
        />
      )}
      {item.mediaUrl && item.messageType === 'FILE' && (
        <FileCardUser href={item.mediaUrl} target="_blank" rel="noopener noreferrer">
          <FileIconWrapUser>
            <Icon name="link" size={16} />
          </FileIconWrapUser>
          <FileName>{getFileNameFromUrl(item.mediaUrl)}</FileName>
        </FileCardUser>
      )}
      {item.body && (
        <NoteBody>
          <MarkdownText>{item.body}</MarkdownText>
        </NoteBody>
      )}
    </NoteCard>
  );
};

// ── ThreadItem (main export) ───────────────────────────────────────────

const ThreadItem = ({ item, emailFrom, emailTo, onOptionSelect, userOnRight }) => {
  if (!item) return null;

  switch (item.kind) {
    case 'event':
      return <EventItem item={item} />;
    case 'msg':
      return (
        <MsgItem
          item={item}
          emailFrom={emailFrom}
          emailTo={emailTo}
          onOptionSelect={onOptionSelect}
          userOnRight={userOnRight}
        />
      );
    case 'system':
      return <SystemItem item={item} />;
    case 'note':
      return <NoteItem item={item} />;
    default:
      return null;
  }
};

export default ThreadItem;
